import type { DirectoryImport, DirectorySource, DirectoryTierCode, LetterOnlyBrand } from '../../services/brandDealerDirectoryApi';
import { PRODUCT_CATEGORIES, designationTierLabel } from '../../utils/brandAuthorization';
import type { DealerLocatorClaimStore, DealerLocatorSourceRow } from '../../services/dealerLocatorApi';

export const LIST_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

export type CombinedRow = {
  brandId: string;
  brandName: string;
  notes: string;
  ingested: boolean;
  covers: string[];
  publicUrl?: string;
  /** Published dealer PDF, kept beside a live locator when the brand has both. */
  pdfUrl?: string;
  locatorUrl?: string;
  hasLocator: boolean;
  claimTime: boolean;
  fetchable: boolean;
  uploadable: boolean;
  imported?: DirectoryImport;
  claims: { unverified: number; pending: number; verified: number };
  stores: DealerLocatorClaimStore[];
  kind: 'list_locator' | 'list' | 'locator' | 'letter' | 'api_pending';
  /**
   * True for a brand with no row in either registry at all — no published
   * list, no locator, and no admin-curated "letter only" entry either. It is
   * letter-only purely because nothing else is wired up.
   */
  noRegistry?: boolean;
  logoUrl?: string;
  /** Tier codes Motzu reads off this brand's list or locator into designations */
  tierCodes?: DirectoryTierCode[];
};

/** How a brand's dealers are checked, in the words the Dealer lists page uses. */
export const dealerKindLabel: Record<CombinedRow['kind'], string> = {
  list_locator: 'List + locator',
  list: 'Authorized list',
  locator: 'Store locator',
  letter: 'Proof only',
  api_pending: 'Member API',
};

const emptyClaims = { unverified: 0, pending: 0, verified: 0 };

function addClaims(a: CombinedRow['claims'], b: CombinedRow['claims']): CombinedRow['claims'] {
  return {
    unverified: a.unverified + b.unverified,
    pending: a.pending + b.pending,
    verified: a.verified + b.verified,
  };
}

function mergeStores(
  a: DealerLocatorClaimStore[],
  b: DealerLocatorClaimStore[],
): DealerLocatorClaimStore[] {
  const seen = new Set(a.map(store => store.organizationId));
  const next = [...a];
  for (const store of b) {
    if (seen.has(store.organizationId)) continue;
    seen.add(store.organizationId);
    next.push(store);
  }
  return next;
}

function absorbChild(parent: CombinedRow, child: CombinedRow): void {
  parent.claims = addClaims(parent.claims, child.claims);
  parent.stores = mergeStores(parent.stores, child.stores);
  parent.hasLocator = parent.hasLocator || child.hasLocator;
  parent.claimTime = parent.claimTime || child.claimTime;
  if (child.imported && !parent.imported) parent.imported = child.imported;
  if (child.locatorUrl && !parent.locatorUrl) parent.locatorUrl = child.locatorUrl;
  if (!parent.covers.includes(child.brandId)) parent.covers = [...parent.covers, child.brandId];
  if (parent.kind === 'list' && child.claimTime) parent.kind = 'list_locator';
  if (parent.kind === 'letter' && child.claimTime) parent.kind = 'locator';
}

/** The file URL when the brand publishes a dealer PDF, not only a web page. */
export function publishedPdfUrl(
  source: { listUrl?: string },
  imported?: { sourceUrl?: string | null },
): string | undefined {
  for (const url of [source.listUrl, imported?.sourceUrl || undefined]) {
    if (url && /\.pdf(?:$|\?)/i.test(url)) return url;
  }
  return undefined;
}

/** A published / imported dealer list — not just a directory status of authorized. */
export function hasPublishedList(
  source: Pick<DirectorySource, 'listUrl' | 'parser'>,
  imported?: DirectoryImport,
): boolean {
  return Boolean(imported || source.listUrl || source.parser);
}

export function listReferenceMs(imported?: DirectoryImport): number | null {
  if (!imported) return null;
  if (imported.listDate && /^\d{4}-\d{2}-\d{2}$/.test(imported.listDate)) {
    const parsed = Date.parse(`${imported.listDate}T00:00:00.000Z`);
    if (!Number.isNaN(parsed)) return parsed;
  }
  if (imported.importedAt) {
    const parsed = Date.parse(imported.importedAt);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
}

/**
 * Text for the row's public link, so it never claims more than the row's own
 * label. Only a brand with a published list is "Open list"; a locator is
 * "Open locator"; a letter-only brand whose registry entry still carries a
 * page (a dealer finder, a "where to buy" page) is "Open brand page", because
 * that page is a reference to look a store up on, not a list Motzu can read.
 */
export function publicLinkLabel(row: Pick<CombinedRow, 'kind'>): string {
  switch (row.kind) {
    case 'list':
    case 'list_locator':
      return 'Open list';
    case 'locator':
      return 'Open locator';
    default:
      return 'Open brand page';
  }
}

/** Same rule as backend `isDealerListFresh`: reject missing and future dates. */
export function listIsFresh(imported?: DirectoryImport, now = Date.now()): boolean {
  const ref = listReferenceMs(imported);
  if (ref === null) return false;
  const age = now - ref;
  return age >= 0 && age <= LIST_MAX_AGE_MS;
}

/**
 * Rows for catalog brands with no row in either registry (see
 * `LetterOnlyBrand`). Always `kind: 'letter'`, never fetchable or
 * uploadable — there is no source to fetch or replace, only the store's own
 * letter upload on its brand authorization record.
 */
export function letterOnlyFallbackRows(brands: LetterOnlyBrand[]): CombinedRow[] {
  return brands.map(brand => ({
    brandId: brand.brandId,
    brandName: brand.brandName,
    notes: '',
    ingested: false,
    covers: [],
    hasLocator: false,
    claimTime: false,
    fetchable: false,
    uploadable: false,
    claims: brand.claims,
    stores: brand.stores,
    kind: 'letter',
    noRegistry: true,
  }));
}

export function mergeRows(
  directory: DirectorySource[],
  imports: Record<string, DirectoryImport>,
  locators: DealerLocatorSourceRow[],
  claimSummaries?: {
    claims?: Record<string, CombinedRow['claims']>;
    stores?: Record<string, DealerLocatorClaimStore[]>;
  },
  letterOnlyBrands: LetterOnlyBrand[] = [],
): CombinedRow[] {
  const byId = new Map<string, CombinedRow>();
  directory.forEach(source => {
    const imported = imports[source.brandId];
    const hasList = hasPublishedList(source, imported);
    byId.set(source.brandId, {
      brandId: source.brandId,
      brandName: source.brandName,
      notes: source.notes || '',
      ingested: Boolean(source.ingested),
      covers: source.coversBrandIds || [],
      publicUrl: source.url || source.listUrl,
      pdfUrl: publishedPdfUrl(source, imported),
      locatorUrl: source.status === 'locator' ? source.url : undefined,
      hasLocator: source.status === 'locator',
      claimTime: false,
      fetchable: Boolean(source.parser),
      uploadable: source.status !== 'none',
      imported,
      claims: source.claims || emptyClaims,
      stores: source.stores || [],
      kind: source.status === 'api_pending' ? 'api_pending' : hasList ? 'list' : source.status === 'locator' ? 'locator' : 'letter',
      logoUrl: source.logoUrl,
      tierCodes: source.tierCodes || [],
    });
  });
  locators.forEach(locator => {
    const existing = byId.get(locator.brandId);
    const parent = directory.find(source => (source.coversBrandIds || []).includes(locator.brandId));
    const inheritedImport = imports[locator.brandId] || (parent ? imports[parent.brandId] : undefined);
    const next: CombinedRow = existing || {
      brandId: locator.brandId,
      brandName: locator.brandName,
      notes: locator.notes,
      ingested: Boolean(parent?.ingested),
      covers: [],
      publicUrl: locator.publicUrl || parent?.url || parent?.listUrl,
      pdfUrl: publishedPdfUrl(parent || {}, inheritedImport),
      hasLocator: locator.claimTime,
      claimTime: locator.claimTime,
      fetchable: false,
      uploadable: false,
      imported: inheritedImport,
      claims: claimSummaries?.claims?.[locator.brandId] || emptyClaims,
      stores: claimSummaries?.stores?.[locator.brandId] || [],
      kind: hasPublishedList(parent || {}, inheritedImport)
        ? 'list'
        : locator.claimTime
          ? 'locator'
          : 'letter',
      logoUrl: parent?.logoUrl,
    };
    next.hasLocator = next.hasLocator || locator.claimTime;
    next.claimTime = locator.claimTime;
    next.locatorUrl = locator.publicUrl || next.locatorUrl;
    next.notes = next.notes || locator.notes;
    if (next.kind === 'list' && locator.claimTime) next.kind = 'list_locator';
    if (next.kind === 'letter' && locator.claimTime) next.kind = 'locator';
    byId.set(locator.brandId, next);
  });
  // Fold covered siblings (Lumix, Lowel) onto the program parent so Alex
  // does not see the same locator twice. Jordan still claims each brand.
  const folded = new Set<string>();
  for (const [id, row] of Array.from(byId.entries())) {
    const parent = directory.find(source => (source.coversBrandIds || []).includes(id));
    const parentRow = parent ? byId.get(parent.brandId) : undefined;
    if (!parentRow || parentRow === row) continue;
    absorbChild(parentRow, row);
    byId.delete(id);
    folded.add(id);
  }
  for (const row of Array.from(byId.values())) {
    for (const childId of row.covers) {
      if (folded.has(childId)) continue;
      const childClaims = claimSummaries?.claims?.[childId];
      const childStores = claimSummaries?.stores?.[childId];
      if (childClaims) row.claims = addClaims(row.claims, childClaims);
      if (childStores) row.stores = mergeStores(row.stores, childStores);
    }
  }
  // Fallback brands have no registry row by construction, so they never
  // collide with an id already in byId; append them after folding.
  for (const row of letterOnlyFallbackRows(letterOnlyBrands)) {
    if (!byId.has(row.brandId)) byId.set(row.brandId, row);
  }

  return Array.from(byId.values()).sort((a, b) => a.brandName.localeCompare(b.brandName));
}

/**
 * "NPD → Premier (Imaging) · NID → base authorization": what each code on
 * the brand's list or locator becomes. Empty when Motzu reads no tiers.
 */
export function tierCodesSummary(row: Pick<CombinedRow, 'tierCodes'>): string {
  return (row.tierCodes || [])
    .map(rule => {
      if (!rule.tiers.length) return `${rule.code} → base authorization`;
      const scope = (rule.categoryScope || []).map(c => PRODUCT_CATEGORIES.find(p => p.value === c)?.label || c).join(', ');
      return `${rule.code} → ${rule.tiers.map(designationTierLabel).join(' + ')}${scope ? ` (${scope})` : ''}`;
    })
    .join(' · ');
}

/** Lowercase letters and digits only, so `peak_design`, `peak-design` and `Peak Design` meet. */
export const dealerBrandKey = (value: string | null | undefined): string =>
  String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Finds the Dealer lists row that verifies a brand, by its id, slug, name or
 * aliases. A brand a program covers (Lumix under Panasonic, Lowel under
 * Tiffen) resolves to the program's row, as `mergeRows` folds it there.
 */
export function dealerRowLookup(
  rows: CombinedRow[],
  sources: Array<Pick<DirectorySource, 'brandId' | 'aliases'>> = [],
): (brand: { id: string; slug?: string; name: string; aliases?: string[] }) => CombinedRow | undefined {
  const byKey = new Map<string, CombinedRow>();
  const add = (key: string, row: CombinedRow) => {
    const normalized = dealerBrandKey(key);
    if (normalized && !byKey.has(normalized)) byKey.set(normalized, row);
  };
  for (const row of rows) add(row.brandId, row);
  for (const row of rows) {
    add(row.brandName, row);
    row.covers.forEach(child => add(child, row));
  }
  for (const source of sources) {
    const row = byKey.get(dealerBrandKey(source.brandId));
    if (row) (source.aliases || []).forEach(alias => add(alias, row));
  }
  return brand => {
    for (const key of [brand.id, brand.slug, brand.name, ...(brand.aliases || [])]) {
      const row = byKey.get(dealerBrandKey(key));
      if (row) return row;
    }
    return undefined;
  };
}
